import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Check } from '@mui/icons-material';

import {
  useArnoFieldUsageDetail,
  useSetArnoMappingDecision,
} from '../../hooks/reactQuerys/ArnoAdminQueries';
import {
  ancestorBlockers,
  branchPairs,
  EVIDENCE_LABEL,
  impactSentence,
  reachSentence,
  isReadyToConfirm,
  MAPPING_KIND_LABEL,
  stateChip,
  valueSummary,
} from './fieldEvidence';
import { SelectorPicker } from './SelectorPicker';
import { useAlert } from '../../context/AlertContext';

/**
 * Ein Legacy-Feld mit allem, was über seine Verwendung gemessen wurde (#1245).
 *
 * Die Reihenfolge ist Absicht: erst die Messwerte, dann die Beurteilung, zuletzt
 * die Schaltfläche. Wer bestätigt, hat vorher gesehen, woran die Bedeutung
 * hängt — ein Feld heißt nicht deshalb „Anzahl", weil „ANZ" darin vorkommt.
 */

interface Props {
  sourceKey: string | null;
  onClose: () => void;
}

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <Box sx={{ mt: 2 }}>
    <Typography variant='overline' color='text.secondary'>
      {title}
    </Typography>
    {children}
  </Box>
);

export const FieldEvidenceDrawer: React.FC<Props> = ({
  sourceKey,
  onClose,
}) => {
  const { showAlert } = useAlert();
  const { fieldUsageDetail, isFetchingFieldUsageDetail } =
    useArnoFieldUsageDetail(sourceKey);
  const { setMappingDecision, isSettingMappingDecision } =
    useSetArnoMappingDecision();
  const [targetPath, setTargetPath] = useState('');

  useEffect(() => {
    setTargetPath(
      fieldUsageDetail?.targetPath ?? fieldUsageDetail?.candidate ?? ''
    );
  }, [fieldUsageDetail]);

  const branches = useMemo(
    () => branchPairs(fieldUsageDetail),
    [fieldUsageDetail]
  );
  const blockers = useMemo(
    () => ancestorBlockers(fieldUsageDetail),
    [fieldUsageDetail]
  );

  const confirm = async () => {
    if (!fieldUsageDetail || !targetPath.trim()) {
      return;
    }
    await setMappingDecision({
      sourceType: 'DOCVARIABLE',
      sourceKey: fieldUsageDetail.sourceKey,
      scope: 'tenant',
      status: 'CONFIRMED',
      targetPath: targetPath.trim(),
      note: null,
    });
    showAlert({
      severity: 'success',
      text: `${fieldUsageDetail.sourceKey} ist auf ${targetPath.trim()} zugeordnet. Die Wirkung erscheint nach der nächsten Messung.`,
    });
    onClose();
  };

  return (
    <Drawer
      anchor='right'
      open={Boolean(sourceKey)}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', md: 620 }, p: 3 } }}
    >
      {isFetchingFieldUsageDetail && <CircularProgress size={22} />}
      {fieldUsageDetail && (
        <Box>
          <Stack direction='row' spacing={1} alignItems='center'>
            <Typography variant='h6' sx={{ fontFamily: 'monospace' }}>
              {fieldUsageDetail.sourceKey}
            </Typography>
            <Chip
              size='small'
              color={stateChip(fieldUsageDetail.state).color}
              label={stateChip(fieldUsageDetail.state).label}
            />
            <Chip
              size='small'
              variant='outlined'
              label={EVIDENCE_LABEL[fieldUsageDetail.evidenceStatus]}
            />
          </Stack>

          <Typography variant='body2' sx={{ mt: 1 }}>
            {impactSentence(fieldUsageDetail)}
          </Typography>
          {reachSentence(fieldUsageDetail) && (
            <Typography variant='body2' color='text.secondary' sx={{ mt: 0.5 }}>
              {reachSentence(fieldUsageDetail)}
            </Typography>
          )}
          {blockers.length > 0 && (
            <Section title='Aufgehalten von'>
              <Typography variant='caption' color='text.secondary'>
                Diese äußeren Bedingungen stehen über den Stellen dieses Feldes.
                Solange sie offen bleiben, wirkt eine Zuordnung hier nicht im
                Klon.
              </Typography>
              <Stack spacing={0.5} sx={{ mt: 1 }}>
                {blockers.map(entry => (
                  <Typography
                    key={entry.label}
                    variant='body2'
                    sx={{ fontFamily: 'monospace' }}
                  >
                    {entry.label} · {entry.count}
                  </Typography>
                ))}
              </Stack>
            </Section>
          )}

          <Section title='Verwendung'>
            <Typography variant='body2'>
              {fieldUsageDetail.occurrences} Vorkommen, davon{' '}
              {fieldUsageDetail.plainUses} sichtbar ausgegeben und{' '}
              {fieldUsageDetail.ifUses} als linke Seite einer Word-Bedingung.
            </Typography>
            {fieldUsageDetail.detail.spellings &&
              fieldUsageDetail.detail.spellings.length > 1 && (
                <Typography variant='caption' color='text.secondary'>
                  Schreibweisen:{' '}
                  {fieldUsageDetail.detail.spellings
                    .map(([name, count]) => `${name} (${count})`)
                    .join(' · ')}
                </Typography>
              )}
          </Section>

          <Section title='Vergleichswerte'>
            <Typography variant='body2'>
              {valueSummary(fieldUsageDetail.detail.values) || '–'}
            </Typography>
            <Typography variant='caption' color='text.secondary'>
              Operatoren:{' '}
              {Object.entries(fieldUsageDetail.detail.operators ?? {})
                .map(([operator, count]) => `${operator} (${count})`)
                .join(' · ') || '–'}
            </Typography>
          </Section>

          {branches.length > 0 && (
            <Section title='Zweigtexte'>
              {/* Die Gegenüberstellung ist der eigentliche Beweis: an „ihr/ihm"
                  liest man ein Geschlecht ab, an „erschien/erschienen" eine Anzahl. */}
              <Table size='small'>
                <TableHead>
                  <TableRow>
                    <TableCell>Trifft zu</TableCell>
                    <TableCell>Trifft nicht zu</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {branches.map((pair, index) => (
                    <TableRow key={`${pair.trueText}-${index}`}>
                      <TableCell sx={{ fontFamily: 'monospace' }}>
                        {pair.trueText || '–'}
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace' }}>
                        {pair.falseText || '–'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Section>
          )}

          {(fieldUsageDetail.detail.contexts ?? []).length > 0 && (
            <Section title='Beispielkontexte'>
              {(fieldUsageDetail.detail.contexts ?? []).map(context => (
                <Typography
                  key={context}
                  variant='caption'
                  component='div'
                  sx={{ opacity: 0.8 }}
                >
                  {context}
                </Typography>
              ))}
            </Section>
          )}

          <Divider sx={{ mt: 2 }} />

          <Section title='Beurteilung'>
            <Typography variant='body2'>
              {fieldUsageDetail.semantics ??
                'Für dieses Feld liegt keine geprüfte Bedeutung vor.'}
            </Typography>
            {fieldUsageDetail.evidence.map(line => (
              <Typography
                key={line}
                variant='caption'
                component='div'
                color='text.secondary'
              >
                • {line}
              </Typography>
            ))}
            {fieldUsageDetail.reason && (
              <Alert
                severity={
                  fieldUsageDetail.state === 'READY_TO_CONFIRM'
                    ? 'success'
                    : 'info'
                }
                sx={{ mt: 1 }}
              >
                {fieldUsageDetail.reason}
              </Alert>
            )}
            <Typography variant='caption' color='text.secondary'>
              Art der Zuordnung:{' '}
              {MAPPING_KIND_LABEL[fieldUsageDetail.mappingKind]}
            </Typography>
          </Section>

          {isReadyToConfirm(fieldUsageDetail) ? (
            <Section title='Zuordnen'>
              <SelectorPicker value={targetPath} onChange={setTargetPath} />
              <Button
                variant='contained'
                startIcon={<Check />}
                disabled={!targetPath.trim() || isSettingMappingDecision}
                onClick={confirm}
                sx={{ mt: 2 }}
              >
                Zuordnung bestätigen
              </Button>
              <Typography
                variant='caption'
                color='text.secondary'
                component='div'
                sx={{ mt: 1 }}
              >
                Bestehende Entscheidungsbäume werden dadurch nicht verändert.
                Ein Klon übernimmt die Zuordnung, wenn er neu erzeugt oder die
                Migration erneut geprüft wird.
              </Typography>
            </Section>
          ) : (
            <Alert severity='warning' sx={{ mt: 2 }}>
              {fieldUsageDetail.state === 'CONFIRMED'
                ? `Bereits entschieden: ${fieldUsageDetail.targetPath}`
                : 'Ohne belegte Bedeutung und vorhandenes DiNo-Feld wird hier nichts zugeordnet.'}
            </Alert>
          )}
        </Box>
      )}
    </Drawer>
  );
};
